import React, { useEffect, useMemo, useState } from 'react'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { ISapConnection, ISiteControlHistoryModel, ISiteControlModel, ISiteTimerHistoryModel, ISiteTimerModel } from '../models'
import { useCustomReducer } from '../../../../../base/customHooks'
import { GetSiteControlApi } from '../services/getSiteControlApi'
import IPCheckbox from '../../../../../components/IPCheckBox'
import IPModal from '../../../../../components/IPModal'
import IPInput from '../../../../../components/IPInput'
import { UpdateSiteControlApi } from '../services/updateSiteControlApi'
import Swal from 'sweetalert2'
import { GetSiteControlHistoryApi } from '../services/getSiteControlHistoryApi'
import moment from 'moment'
import IPButton from '../../../../../components/IPButton'
import IPDatePicker from '../../../../../components/IPDatePicker'
import { UpdateTimerApi } from '../services/updateTimerApi'
import InputCampaign from '../../../../../components/InputCampaign'
import { getDateByTimezoneOffset, toDateTimeLocal } from '../../../../../base/utils/dataHelper'
import { GetTimerApi } from '../services/getTimerApi'
import { DeleteTimerApi } from '../services/deleteTimerApi'
import { GetSapConnectionApi } from '../services/getSapConnectionApi'
import Table from '../../../../../components/Table'
import './SiteDataTables.scss'

interface ISiteControlState {
  showModal: boolean
  showHistoryModal: boolean
  showTimerModal: boolean
  payment?: boolean
  iyzico?: boolean
  compay?: boolean
  preprod?: boolean
  order?: boolean
  preprodLogin?: boolean
  siteLogin?: boolean
  generalNote?: string
  paymentNote?: string
  preprodNote?: string
  iyzicoNote?: string
  compayNote?: string
  orderNote?: string
  siteLoginNote?: string
  preprodLoginNote?: string
  startDate?: string
  endDate?: string
  paymentTL?: boolean
  paymentUSD?: boolean
  paymentEUR?: boolean
  paymentTLNote?: string
  paymentUSDNote?: string
  paymentEURNote?: string
}

function Index(props: BaseComponentWithContextProps) {
  const getInitialState = (): ISiteControlState => {
    return {
      showModal: false,
      showHistoryModal: false,
      showTimerModal: false,
    }
  }

  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<ISiteControlState>(intialState)
  const [siteData, setSiteData] = useState<ISiteControlModel>()
  const [timerData, setTimerData] = useState<ISiteTimerModel[]>()
  const [connection, setConnection] = useState<ISapConnection>()
  const [historyData, setHistoryData] = useState<ISiteControlHistoryModel[]>()

  // Tarihçe satırındaki notlar tek tek kolon yerine açılır panelde gösterilir.
  const [expandedHistoryRow, setExpandedHistoryRow] = useState<number | null>(null)

  const historyStatus = (value?: boolean) => (
    <span className={`statusText ${value ? 'open' : 'closed'}`}>{value ? 'Açık' : 'Kapalı'}</span>
  )

  const getHistoryNotes = (history: ISiteControlHistoryModel) =>
    [
      { label: 'Sipariş', note: history.NoteOrder },
      { label: 'İyzico', note: history.NoteIyzico },
      { label: 'Compay', note: history.NoteCompay },
      { label: 'Ödeme', note: history.NotePayment },
      { label: 'TL Ödeme', note: history.NotePaymentTL },
      { label: 'USD Ödeme', note: history.NotePaymentUSD },
      { label: 'EUR Ödeme', note: history.NotePaymentEUR },
      { label: 'Preprod', note: history.NotePreprod },
      { label: 'Preprod Giriş', note: history.PreprodLoginNote },
      { label: 'Indexgruppazar Giriş', note: history.SiteLoginNote },
      { label: 'Genel Not', note: history.Notes },
    ].filter(item => item.note && item.note.trim().length > 0)

  const historyColumns = useMemo(
    () => [
      {
        Header: 'Tarih',
        accessor: 'date',
        // Hücrede gün.ay.yıl metni durduğu için sıralama ham zaman damgası üzerinden yapılır.
        sortType: (rowA: any, rowB: any) => {
          const a = rowA.original.dateValue
          const b = rowB.original.dateValue
          return a > b ? 1 : a < b ? -1 : 0
        },
      },
      { Header: 'Bayi', accessor: 'dealer' },
      { Header: 'Kullanıcı Adı', accessor: 'userName' },
      { Header: 'Sipariş', accessor: 'order' },
      { Header: 'İyzico', accessor: 'iyzico' },
      { Header: 'Compay', accessor: 'compay' },
      { Header: 'Ödemeler', accessor: 'payment' },
      { Header: 'Ödemeler (TL)', accessor: 'paymentTL' },
      { Header: 'Ödemeler (USD)', accessor: 'paymentUSD' },
      { Header: 'Ödemeler (EUR)', accessor: 'paymentEUR' },
      { Header: 'Preprod', accessor: 'preprod' },
      { Header: 'Preprod Giriş', accessor: 'preprodLogin' },
      { Header: 'Indexgruppazar Giriş', accessor: 'siteLogin' },
      { Header: 'Notlar', accessor: 'notes' },
    ],
    []
  )

  const historyRows = useMemo(() => {
    if (!historyData) return []

    return historyData.map((history, index) => {
      const notes = getHistoryNotes(history)
      const isExpanded = expandedHistoryRow === index

      return {
        date: history.UpdateTime ? moment(history.UpdateTime).format('DD.MM.YYYY HH:mm:ss') : '-',
        dateValue: history.UpdateTime ? new Date(history.UpdateTime).getTime() : 0,
        dealer: history.UserDealer ? history.UserDealer.replace('0000', '') : '-',
        userName: history.UserName || '-',
        order: historyStatus(history.Order),
        iyzico: historyStatus(history.Iyzico),
        compay: historyStatus(history.Compay),
        payment: historyStatus(history.Payment),
        // Döviz kolonları eklenmeden önceki tarihçe satırlarında kısıt yoktu: Açık gösterilir.
        paymentTL: historyStatus(history.PaymentTL ?? true),
        paymentUSD: historyStatus(history.PaymentUSD ?? true),
        paymentEUR: historyStatus(history.PaymentEUR ?? true),
        preprod: historyStatus(history.Preprod),
        preprodLogin: historyStatus(history.PreprodLogin),
        siteLogin: historyStatus(history.SiteLogin),
        notes:
          notes.length === 0 ? (
            <span className='siteHistoryNoNote'>-</span>
          ) : (
            <div className='siteHistoryNotes'>
              <button
                type='button'
                className='siteHistoryNotesToggle'
                onClick={() => setExpandedHistoryRow(isExpanded ? null : index)}
              >
                <i className={isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'} />
                {`Notlar (${notes.length})`}
              </button>
              {isExpanded && (
                <div className='siteHistoryNotesPanel'>
                  {notes.map(item => (
                    <div className='siteHistoryNoteItem' key={item.label}>
                      <b>{item.label}:</b> <span>{item.note}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ),
      }
    })
  }, [historyData, expandedHistoryRow])


  const getSiteData = () => {
    const res = new GetSiteControlApi({}).getSiteControl()
    res.then((result) => {
      if (result) {
        setSiteData(result)
        setState({
          order: result.Order,
          iyzico: result.Iyzico,
          compay: result.Compay,
          preprod: result.Preprod,
          payment: result.Payment,
          paymentNote: result.NotePayment,
          iyzicoNote: result.NoteIyzico,
          compayNote: result.NoteCompay,
          preprodNote: result.NotePreprod,
          orderNote: result.NoteOrder,
          generalNote: result.Notes,
          preprodLogin: result.PreprodLogin,
          preprodLoginNote: result.PreprodLoginNote,
          siteLogin: result.SiteLogin,
          siteLoginNote: result.SiteLoginNote,
          // Kolon henüz doldurulmamışsa kısıt yok kabul edilir (backend ile aynı kural).
          paymentTL: result.PaymentTL ?? true,
          paymentUSD: result.PaymentUSD ?? true,
          paymentEUR: result.PaymentEUR ?? true,
          paymentTLNote: result.NotePaymentTL,
          paymentUSDNote: result.NotePaymentUSD,
          paymentEURNote: result.NotePaymentEUR,
        })
      }
    }
    ).catch((err: any) => {
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }

  const getTimerData = () => {
    const res = new GetTimerApi({}).getSiteTimer()
    res.then((result) => {
      if (result) {
        setTimerData(result)
      }
    }
    ).catch((err: any) => {
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }

  const getSapConnection = () => {
    const res = new GetSapConnectionApi({}).getSapConnection()
    res.then((result) => {
      if (result) {
        setConnection(result)
      }
    }
    ).catch((err: any) => {
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }

  const getSiteDataHistory = () => {
    const res = new GetSiteControlHistoryApi({}).getSiteControlHistory()
    res.then((result) => {
      if (result) {
        setHistoryData(result)
        setState({ showHistoryModal: true })
      }
    }
    ).catch((err: any) => {
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }

  const updateSiteData = () => {
    props.showLoading()
    const req: ISiteControlModel = {
      Order: state.order,
      Iyzico: state.iyzico,
      Compay: state.compay,
      Payment: state.payment,
      Preprod: state.preprod,
      PreprodLogin: state.preprodLogin,
      PreprodLoginNote: state.preprodLoginNote,
      SiteLogin: state.siteLogin,
      SiteLoginNote: state.siteLoginNote,
      NotePreprod: state.preprodNote,
      NotePayment: state.paymentNote,
      NoteIyzico: state.iyzicoNote,
      NoteCompay: state.compayNote,
      NoteOrder: state.orderNote,
      Notes: state.generalNote,
      PaymentTL: state.paymentTL,
      PaymentUSD: state.paymentUSD,
      PaymentEUR: state.paymentEUR,
      NotePaymentTL: state.paymentTLNote,
      NotePaymentUSD: state.paymentUSDNote,
      NotePaymentEUR: state.paymentEURNote
    }

    const res = new UpdateSiteControlApi({}).siteControlUpdate(req)
    res.then((result) => {
      props.hideLoading()
      if (result) {
        Swal.fire({
          icon: 'success',
          title: "Site Çalışma Durumu Başarılı Bir Şekilde Değiştirilmiştir.",
          allowOutsideClick: true,
          backdrop: true,
        })
        setSiteData(result)
        setState({
          order: result.Order,
          iyzico: result.Iyzico,
          compay: result.Compay,
          payment: result.Payment,
          preprod: result.Preprod,
          preprodLogin: result.PreprodLogin,
          preprodLoginNote: result.PreprodLoginNote,
          siteLogin: result.SiteLogin,
          siteLoginNote: result.SiteLoginNote,
          preprodNote: result.NotePreprod,
          paymentNote: result.NotePayment,
          iyzicoNote: result.NoteIyzico,
          compayNote: result.NoteCompay,
          orderNote: result.NoteOrder,
          generalNote: result.Notes,
          paymentTL: result.PaymentTL ?? true,
          paymentUSD: result.PaymentUSD ?? true,
          paymentEUR: result.PaymentEUR ?? true,
          paymentTLNote: result.NotePaymentTL,
          paymentUSDNote: result.NotePaymentUSD,
          paymentEURNote: result.NotePaymentEUR,
          showModal: false
        })
      }
    }
    ).catch((err: any) => {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }

  const updateTimer = () => {
    props.showLoading()
    const req: ISiteTimerModel = {
      StartDate: state.startDate ? getDateByTimezoneOffset(new Date(state.startDate)) : undefined,
      EndDate: state.endDate ? getDateByTimezoneOffset(new Date(state.endDate)) : undefined,
      Active: true,
    }

    const res = new UpdateTimerApi({}).timerUpdate(req)
    res.then((result) => {
      props.hideLoading()
      if (result) {
        Swal.fire({
          icon: 'success',
          title: "Site Çalışma Durumu Başarılı Bir Şekilde Değiştirilmiştir.",
          allowOutsideClick: true,
          backdrop: true,
        })
        setTimerData(result)
        setState({
          startDate: undefined,
          endDate: undefined,
          showTimerModal: false
        })
      }
    }
    ).catch((err: any) => {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }

  const deleteTimer = (id: number) => {
    props.showLoading()

    const res = new DeleteTimerApi({}).timerDelete(id)
    res.then((result) => {
      props.hideLoading()
      if (result) {
        Swal.fire({
          icon: 'success',
          title: "Kapanma Planı Başarılı Bir Şekilde Silinmiştir.",
          allowOutsideClick: true,
          backdrop: true,
        })
        setTimerData(result)
        setState({
          startDate: undefined,
          endDate: undefined,
          showTimerModal: false
        })
      }
    }
    ).catch((err: any) => {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err.description,
        allowOutsideClick: true,
        backdrop: true,
      })
    })
  }


  useEffect(() => {
    getSiteData()
    getTimerData()
    getSapConnection()
  }, [])

  return (
    <div>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header'>
            <div className='row'>
              <div className='col-lg-6' style={{ display: 'grid', alignItems: 'center' }}>
                <span className='h4'>Site Çalışma Durumu</span>
              </div>
              <div className='col-lg-6' style={{ display: 'grid', justifyContent: 'end' }}>
                {
                  connection && (<><span>SAP URL: <b>{connection.ConnectionLink}</b></span><br></br><span>SAP: <b>{connection.ConnectionType}</b></span></>)
                }
              </div>
            </div>


          </div>
          <div className='card-body'>
            <div className='row'>
              {siteData && (
                <>
                  <div className="siteDataInfo">
                    <span className="warn">
                      Sipariş, İyzico ve Ödeme işlemleri için not olmadığı durumda "<b>Genel Not</b>" kullanıcıya gösterilecektir.
                    </span>
                    <span className="warn">
                      Döviz bazlı ödemeler genel "<b>Ödemeler</b>" ayarının altında çalışır: genel ödeme kapalıysa döviz açık olsa da
                      ödeme alınmaz. Kapalı dövizler bayiye "Hesap Cinsi" listesinde hiç gösterilmez. Döviz notu boşsa "
                      <b>Ödeme Notu</b>", o da boşsa "<b>Genel Not</b>" gösterilir.
                    </span>
                    <span className="warn">
                      <b>Planlanmış kapanış tarihleri aralığında bütün sistemler kapalı olacaktır.</b>
                    </span>
                    <span className="warn">
                      <b>
                        Preprod işlemlerinin açık olması durumunda tüm bayiler işlem yapabilmektedir. Kapalı olduğunda ise sadece deneme
                        kartının işlem yapmasına izin verilmektedir.
                      </b>
                    </span>
                  </div>

                  {/* ===== İŞLEMLER (Genel Not dahil) ===== */}
                  <div className="card mb-4 siteDataCard">
                    <div className="card-header">
                      <b>İşlemler</b>

                      <div className="siteDataHeaderActions">
                        <a
                          className="actionBtn edit"
                          role="button"
                          title="Güncelle"
                          onClick={() => setState({ showModal: true })}
                        >
                          <i className="icon-edit" />
                        </a>

                        <a
                          className="actionBtn history"
                          role="button"
                          title="Tarihçe"
                          onClick={() => getSiteDataHistory()}
                        >
                          <i className="icon-clock" />
                        </a>
                      </div>
                    </div>

                    <div className="card-body p-0 siteDataTableWrap">
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped mb-0 text-center align-middle equalCols5">
                          <thead>
                            <tr>
                              <th>Sipariş</th>
                              <th>İyzico</th>
                              <th>Compay</th>
                              <th>Ödemeler</th>
                              <th></th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td>
                                <span className={`statusText ${siteData.Order ? 'open' : 'closed'}`}>
                                  {siteData.Order ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${siteData.Iyzico ? 'open' : 'closed'}`}>
                                  {siteData.Iyzico ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${siteData.Compay ? 'open' : 'closed'}`}>
                                  {siteData.Compay ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${siteData.Payment ? 'open' : 'closed'}`}>
                                  {siteData.Payment ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>
                              <td></td>
                            </tr>

                            <tr className="text-center">
                              <th>Sipariş Notu</th>
                              <th>İyzico Notu</th>
                              <th>Compay Notu</th>
                              <th>Ödeme Notu</th>
                              <th>Genel Not</th>
                            </tr>

                            <tr>
                              <td className="noteCell">{siteData.NoteOrder || '-'}</td>
                              <td className="noteCell">{siteData.NoteIyzico || '-'}</td>
                              <td className="noteCell">{siteData.NoteCompay || '-'}</td>
                              <td className="noteCell">{siteData.NotePayment || '-'}</td>
                              <td className="noteCell">{siteData.Notes || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* ===== DÖVİZ BAZLI ÖDEMELER (Sadece 3 kolon) ===== */}
                  <div className="card mb-4 siteDataCard">
                    <div className="card-header">
                      <b>Döviz Bazlı Ödemeler</b>
                    </div>

                    <div className="card-body p-0 siteDataTableWrap">
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped mb-0 text-center align-middle equalCols3">
                          <thead>
                            <tr>
                              <th>TL</th>
                              <th>USD</th>
                              <th>EUR</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td>
                                <span className={`statusText ${(siteData.PaymentTL ?? true) ? 'open' : 'closed'}`}>
                                  {(siteData.PaymentTL ?? true) ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${(siteData.PaymentUSD ?? true) ? 'open' : 'closed'}`}>
                                  {(siteData.PaymentUSD ?? true) ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${(siteData.PaymentEUR ?? true) ? 'open' : 'closed'}`}>
                                  {(siteData.PaymentEUR ?? true) ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>
                            </tr>

                            <tr className="text-center">
                              <th>TL Notu</th>
                              <th>USD Notu</th>
                              <th>EUR Notu</th>
                            </tr>

                            <tr>
                              <td className="noteCell">{siteData.NotePaymentTL || '-'}</td>
                              <td className="noteCell">{siteData.NotePaymentUSD || '-'}</td>
                              <td className="noteCell">{siteData.NotePaymentEUR || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* ===== GİRİŞ (Sadece 3 kolon) ===== */}
                  <div className="card siteDataCard">
                    <div className="card-header">
                      <b>Giriş Bilgileri</b>
                    </div>

                    <div className="card-body p-0 siteDataTableWrap">
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped mb-0 text-center align-middle equalCols3">
                          <thead>
                            <tr>
                              <th>Preprod</th>
                              <th>Preprod Giriş</th>
                              <th>Indexgruppazar Giriş</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td>
                                <span className={`statusText ${siteData.Preprod ? 'open' : 'closed'}`}>
                                  {siteData.Preprod ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${siteData.PreprodLogin ? 'open' : 'closed'}`}>
                                  {siteData.PreprodLogin ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>

                              <td>
                                <span className={`statusText ${siteData.SiteLogin ? 'open' : 'closed'}`}>
                                  {siteData.SiteLogin ? 'Açık' : 'Kapalı'}
                                </span>
                              </td>
                            </tr>

                            <tr className="text-center">
                              <th>Preprod Notu</th>
                              <th>Preprod Giriş Notu</th>
                              <th>Indexgruppazar Giriş Notu</th>
                            </tr>

                            <tr>
                              <td className="noteCell">{siteData.NotePreprod || '-'}</td>
                              <td className="noteCell">{siteData.PreprodLoginNote || '-'}</td>
                              <td className="noteCell">{siteData.SiteLoginNote || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <hr></hr>
            <br></br>
            <div className='row'>
              <div className='row' style={{ alignContent: 'center', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className='col-lg-3'>
                  <b>Planlanan Kapanış Tarihleri (Sipariş - Ödeme - Login)</b>
                </div>
                <div className='col-lg-2'>
                  <div className='buttonsuz'>
                    <IPButton
                      type='button'
                      className='btn btn-danger'
                      text={'Kapanış Planla'}
                      onClick={() => {
                        setState({ showTimerModal: true })
                      }}
                    />
                  </div>
                </div>
              </div>
              {timerData &&
                <>
                  <div className='table mt-3'>
                    <table className='table table-bordered' style={{ textAlign: 'center', alignItems: 'center', alignContent: 'center' }}>
                      <thead>
                        <tr>
                          <th>Başlangıç</th>
                          <th>Bitiş</th>
                          <th>Ekleyen</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timerData.map(timerData => (
                          <tr>
                            <td>{moment.parseZone(timerData.StartDate).format('DD.MM.YYYY HH:mm:ss')}</td>
                            <td>{moment.parseZone(timerData.EndDate).format('DD.MM.YYYY HH:mm:ss')}</td>
                            <td>
                              {timerData.UserDealer?.replace('0000', '') + ' ' + timerData.UserName}
                            </td>
                            <td>
                              <a onClick={() => { deleteTimer(timerData.Id ?? 0) }}>
                                <i className='icon-trash' style={{ color: 'red', cursor: 'pointer' }}></i>
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              }
            </div>
          </div>
        </div>
        <IPModal
          width={1200}
          show={state.showModal}
          hideConfirm={false}
          title={'Site Durumu Güncelle'}
          onClose={() => {
            setState({ showModal: false })
          }}
          onConfirm={() => {
            updateSiteData()
          }}
          closeOnEscapeKey={true}
        >
          <div className='container'>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Sipariş'
                  checked={state.order}
                  onChange={() => setState({ order: !state.order })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Sipariş Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.orderNote}
                      onChange={(e) => {
                        setState({ orderNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='İyzico'
                  checked={state.iyzico}
                  onChange={() => setState({ iyzico: !state.iyzico })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>İyzico Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.iyzicoNote}
                      onChange={(e) => {
                        setState({ iyzicoNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Ödemeler'
                  checked={state.payment}
                  onChange={() => setState({ payment: !state.payment })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Ödeme Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.paymentNote}
                      onChange={(e) => {
                        setState({ paymentNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Ödemeler (TL)'
                  checked={state.paymentTL}
                  onChange={() => setState({ paymentTL: !state.paymentTL })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>TL Ödeme Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.paymentTLNote}
                      onChange={(e) => {
                        setState({ paymentTLNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Ödemeler (USD)'
                  checked={state.paymentUSD}
                  onChange={() => setState({ paymentUSD: !state.paymentUSD })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>USD Ödeme Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.paymentUSDNote}
                      onChange={(e) => {
                        setState({ paymentUSDNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Ödemeler (EUR)'
                  checked={state.paymentEUR}
                  onChange={() => setState({ paymentEUR: !state.paymentEUR })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>EUR Ödeme Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.paymentEURNote}
                      onChange={(e) => {
                        setState({ paymentEURNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Preprod'
                  checked={state.preprod}
                  onChange={() => setState({ preprod: !state.preprod })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Preprod Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.preprodNote}
                      onChange={(e) => {
                        setState({ preprodNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Compay'
                  checked={state.compay}
                  onChange={() => setState({ compay: !state.compay })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Compay Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.compayNote}
                      onChange={(e) => {
                        setState({ compayNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='PreprodLogin'
                  checked={state.preprodLogin}
                  onChange={() => setState({ preprodLogin: !state.preprodLogin })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Preprod Giriş Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.preprodLoginNote}
                      onChange={(e) => {
                        setState({ preprodLoginNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'>
                <IPCheckbox
                  label='Site'
                  checked={state.siteLogin}
                  onChange={() => setState({ siteLogin: !state.siteLogin })}
                />
              </div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Indexgruppazar Giriş Notu:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.siteLoginNote}
                      onChange={(e) => {
                        setState({ siteLoginNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-lg-2'></div>
              <div className='col-lg-10'>
                <div className='row'>
                  <div className='col-lg-2'>
                    <label>Genel Not:</label>
                  </div>
                  <div className='col-lg-10'>
                    <IPInput
                      maxLength={250}
                      type={'text'}
                      value={state.generalNote}
                      onChange={(e) => {
                        setState({ generalNote: e.target.value })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </IPModal >
        <IPModal
          className='siteHistoryModal'
          show={state.showHistoryModal}
          hideConfirm={true}
          title={'Site Durumu Tarihçe'}
          onClose={() => {
            setState({ showHistoryModal: false })
            setExpandedHistoryRow(null)
          }}
          closeOnEscapeKey={true}
        >
          <Table columns={historyColumns} data={historyRows} />
        </IPModal >
        <IPModal
          width={700}
          height={500}
          show={state.showTimerModal}
          hideConfirm={false}
          confirmText='Ekle'
          title={'Kapanış Planla'}
          onClose={() => {
            setState({ showTimerModal: false })
          }}
          onConfirm={() => {
            updateTimer()
          }}
          closeOnEscapeKey={true}
        >
          <div className='container'>
            <div className='row mt-5'>
              <div className='col-lg-4'>
                <label>Başlangıç Tarihi</label>
                <InputCampaign
                  type='datetime-local'
                  id='StartDate'
                  name='StartDate'
                  label={''}
                  value={state.startDate}
                  onChange={e => setState({ startDate: (e.target.value) })}
                  isActive
                />
              </div>
              <div className='col-lg-4'>
                <label>Bitiş Tarihi</label>
                <InputCampaign
                  type='datetime-local'
                  id='EndDate'
                  name='EndDate'
                  label={''}
                  value={state.endDate}
                  onChange={e => setState({ endDate: (e.target.value) })}
                  isActive
                />
              </div>
            </div>
          </div>
        </IPModal >
      </div >
    </div >
  )
}
export default componentWithContext(Index)